import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import DashboardLayout from "src/components/layout/dashboard/Layout";
import PageHeader from "src/components/common/PageHeader";
import Card from "src/components/common/card/Card";
import Input from "src/components/ui/Input";
import Textarea from "src/components/ui/Textarea";
import Dropdown from "src/components/ui/Dropdown";
import TableV2 from "src/components/ui/TableV2";
import Button from "src/components/ui/Button";
import SearchInput, { SearchInputOption } from "src/components/ui/SearchInput";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as purchaseActions from "src/redux/slices/purchaseSlice";
import { toast } from "react-toastify";
import { currencyFormatter } from "src/utils/common";
import productApis from "src/api/product";
import supplierApis from "src/api/supplier";
import purchaseOrderApis from "src/api/purchaseOrder";
import { PurchaseOrderProduct } from "src/types/purchase.types";
import { Product } from "src/types/product.types";
import { Supplier } from "src/types/supplier.types";
import { Trash } from "lucide-react";

const PurchaseForm: React.FC = () => {
  const { purchaseId } = useParams();
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const isTenantAdmin =
    user?.role === "admin" ||
    user?.roleDetails?.name?.toLowerCase() === "admin";
  const { create, edit } = useAppSelector((s) => s.purchase);

  // ------------------ Supplier ------------------
  const [supplierId, setSupplierId] = useState<string>("");
  const [supplier, setSupplier] = useState<Supplier | null>(null);

  // ------------------ Products ------------------
  const [cart, setCart] = useState<PurchaseOrderProduct[]>([]);

  // ------------------ Summary ------------------
  const [discountPercentage, setDiscountPercentage] = useState<string>("");
  const [paymentStatus, setPaymentStatus] = useState<"paid">("paid");
  const [remarks, setRemarks] = useState("");
  const [orderStatus, setOrderStatus] = useState<
    "draft" | "completed" | "cancelled"
  >("completed");

  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  // ------------------ Load Data ------------------
  useEffect(() => {
    const loadPurchaseDetails = async () => {
      if (!purchaseId) return;
      try {
        setIsLoadingDetails(true);
        const res = await purchaseOrderApis.getDetails(purchaseId);
        const order = res?.data;

        if (!order) {
          toast.error("Purchase order not found");
          navigate("/app/purchase/list");
          return;
        }
        const createdById =
          typeof order?.createdBy === "string"
            ? order.createdBy
            : order?.createdBy?._id;
        if (
          order?.orderStatus === "draft" &&
          !isTenantAdmin &&
          createdById &&
          user?._id &&
          String(createdById) !== String(user._id)
        ) {
          toast.error("Only the creator can edit this draft order");
          navigate("/app/purchase/list");
          return;
        }

        setSupplierId(order?.supplierId?._id || "");
        setSupplier(order?.supplierId || null);
        const loadedStatus = order?.orderStatus || "completed";
        setOrderStatus(loadedStatus);
        setPaymentStatus("paid");
        setRemarks(order?.remarks || "");

        const mappedProducts: PurchaseOrderProduct[] =
          order?.products?.map((item: any) => {
            const productIdValue =
              typeof item.productId === "string"
                ? item.productId
                : item.productId?._id || "";

            return {
              productId: productIdValue,
              name: item.productId?.name || item.name || "",
              sku: item.productId?.sku || item.sku,
              hsnCode: item.productId?.hsnCode || item.hsnCode,
              unitPrice: Number(item.unitPrice || 0),
              taxPercent: Number(
                item.taxPercent ?? item.productId?.gstRate ?? 18
              ),
              quantity: Number(item.quantity || 0),
              totalPrice: Number(item.totalPrice || 0),
            };
          }) || [];

        setCart(mappedProducts);

        if (loadedStatus !== "draft") {
          toast.error("Only draft purchases can be edited");
          navigate("/app/purchase/list");
        }
      } catch (error) {
        console.error("Error fetching order details:", error);
        toast.error("Failed to fetch order details");
      } finally {
        setIsLoadingDetails(false);
      }
    };

    if (purchaseId) {
      loadPurchaseDetails();
    }
  }, [purchaseId, navigate, isTenantAdmin, user?._id]);

  const handleClose = useCallback(() => {
    dispatch(purchaseActions.resetCreatePurchase());
    dispatch(purchaseActions.resetEditPurchase());
    navigate("/app/purchase/list");
  }, [dispatch, navigate]);

  useEffect(() => {
    if (create.data) {
      toast.success("Purchase order created successfully!");
      handleClose();
    }
  }, [create.data, handleClose]);

  useEffect(() => {
    if (edit.data) {
      toast.success("Purchase order updated successfully!");
      handleClose();
    } else if (edit.error) {
      toast.error(`Error: ${edit.error}`);
    }
  }, [edit, handleClose]);

  // ------------------ Cart Logic ------------------
  const handleAddProduct = (product: Product) => {
    if (cart.some((c) => c.productId === product._id)) {
      toast.warning("Product already added");
      return;
    }

    setCart([
      ...cart,
      {
        productId: product._id || "",
        name: product?.name || "",
        sku: product.sku,
        hsnCode: product.hsnCode,
        unitPrice: Number(product.sellingPrice || 0),
        // Defaults from the product's own GST rate, but stays editable per
        // line since a purchase records the supplier's actual invoice.
        taxPercent: Number(product.gstRate ?? 18),
        quantity: 1,
        totalPrice: Number(product.sellingPrice || 0),
      },
    ]);
  };

  const handleUpdateItem = (
    index: number,
    field: keyof PurchaseOrderProduct,
    value: any
  ) => {
    const updated = [...cart];
    // @ts-ignore
    updated[index][field] = value;

    const item = updated[index];
    const qty = parseFloat(String(item.quantity)) || 0;
    const total = qty * (item.unitPrice || 0);
    updated[index].totalPrice = Number(total.toFixed(2));
    setCart(updated);
  };

  const handleRemoveItem = (index: number) => {
    setCart(cart.filter((_, i) => i !== index));
  };

  // ------------------ Totals ------------------
  // Tax is summed per line using each item's own GST %, which defaults from
  // the product but can be overridden to match what the supplier's invoice
  // actually shows.
  const discountPercentValue = parseFloat(discountPercentage) || 0;
  const subTotal = cart.reduce((sum, item) => sum + item.totalPrice, 0);
  const taxValue = cart.reduce(
    (sum, item) => sum + (item.totalPrice * (item.taxPercent || 0)) / 100,
    0
  );
  const discountAmount = (subTotal * discountPercentValue) / 100;
  const grandTotal = subTotal + taxValue - discountAmount;

  // ------------------ Submit ------------------
  const handleSubmit = (status: "completed" | "draft" = "completed") => {
    if (!supplier) {
      toast.error("Please select a supplier");
      return;
    }
    if (cart.length === 0) {
      toast.error("Please add at least one product");
      return;
    }

    const payload = {
      supplierId: supplier?._id || "",
      products: cart,
      remarks,
      createdBy: user?._id || "",
      paymentStatus: "paid" as const,
      orderStatus: status,
    };

    if (purchaseId) {
      dispatch(
        purchaseActions.editPurchaseRequest({
          id: purchaseId,
          data: payload,
        })
      );
      return;
    }

    dispatch(purchaseActions.createPurchaseRequest(payload));
  };

  const pageTitle = purchaseId
    ? "Edit Purchase Order"
    : "Create Purchase Order";

  return (
    <DashboardLayout
      meta={{
        title: purchaseId
          ? "Edit Purchase | Invyntra"
          : "Create Purchase | Invyntra",
        description: "Create purchase orders.",
      }}
    >
      <div className="space-y-4">
        <PageHeader
          title={pageTitle}
          showBack={!!purchaseId}
          breadcrumbData={[
            { label: "Home", path: "/app/dashboard" },
            { label: "Purchases", path: "/app/purchase/list" },
            { label: pageTitle, path: "" },
          ]}
        />

        <Card>
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-800 text-center">
              Purchase Order
            </h2>

            {/* ---------------- SUPPLIER INFO ---------------- */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Supplier Information
              </h3>

              <div className="w-1/2">
                <SearchInput<Supplier>
                  label="Select Supplier"
                  placeholder="Search by supplier name or ID..."
                  value={
                    supplier ? `${supplier.name} (${supplier.supplierId})` : ""
                  }
                  onSearch={async (query) => {
                    const res = await supplierApis.getSupplierList({
                      page: 1,
                      limit: 10,
                      status: "active",
                      search: query,
                    });
                    const list: Supplier[] = res?.data?.data || res?.data || [];
                    return list.map((s) => ({
                      value: s._id || "",
                      label: `${s.name} (${s.supplierId})`,
                      data: s,
                    }));
                  }}
                  onSelect={(option: SearchInputOption<Supplier>) => {
                    setSupplierId(option.value);
                    setSupplier(option.data);
                  }}
                  renderOption={(option) => (
                    <div>
                      <div className="font-medium text-gray-800">
                        {option.data.name}
                      </div>
                      <div className="text-xs text-gray-500">
                        ID: {option.data.supplierId}
                      </div>
                    </div>
                  )}
                />
              </div>

              {supplier && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3 text-sm">
                  <p>
                    <strong>Name:</strong> {supplier.name}
                  </p>
                  <p>
                    <strong>GST:</strong> {supplier.gstNumber || "N/A"}
                  </p>
                  <p>
                    <strong>Email:</strong> {supplier.email || "—"}
                  </p>
                  <p>
                    <strong>Phone:</strong> {supplier.phone || "—"}
                  </p>
                </div>
              )}
            </div>

            {/* ---------------- PRODUCTS ---------------- */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Products
              </h3>

              <SearchInput<Product>
                label="Add Product"
                placeholder="Search by name, SKU, HSN, or product ID..."
                clearOnSelect
                onSearch={async (query) => {
                  const res = await productApis.getProductList({
                    page: 1,
                    limit: 10,
                    search: query,
                  });
                  const list: Product[] = res?.data?.data || res?.data || [];
                  return list.map((p) => ({
                    value: p._id || "",
                    label: `${p.name} (${p.sku || p.productId})`,
                    data: p,
                  }));
                }}
                onSelect={(option: SearchInputOption<Product>) =>
                  handleAddProduct(option.data)
                }
                renderOption={(option) => (
                  <div>
                    <div className="font-medium text-gray-800">
                      {option.data.name}
                    </div>
                    <div className="text-xs text-gray-500">
                      SKU: {option.data.sku || "-"} · HSN:{" "}
                      {option.data.hsnCode || "-"} · ID:{" "}
                      {option.data.productId}
                    </div>
                  </div>
                )}
              />

              {cart.length > 0 && (
                <div className="mt-4">
                  <TableV2
                    columns={[
                      {
                        key: "name",
                        header: "Product",
                        accessor: ({ row }) => (
                          <div>
                            <div className="font-medium text-gray-800">
                              {row.name}
                            </div>
                            <div className="text-xs text-gray-500">
                              SKU: {row.sku || "-"} · HSN: {row.hsnCode || "-"}
                            </div>
                          </div>
                        ),
                      },
                      {
                        key: "quantity",
                        header: "Qty",
                        accessor: ({ row, rowIndex }) => (
                          <input
                            type="text"
                            className="w-16 border rounded-md px-2 py-1 text-sm text-center"
                            value={row.quantity}
                            onChange={(e) =>
                              handleUpdateItem(
                                rowIndex,
                                "quantity",
                                e.target.value
                              )
                            }
                          />
                        ),
                      },
                      {
                        key: "unitPrice",
                        header: "Price",
                        accessor: ({ row, rowIndex }) => (
                          <input
                            type="text"
                            className="w-16 border rounded-md px-2 py-1 text-sm text-center"
                            value={row.unitPrice}
                            onChange={(e) =>
                              handleUpdateItem(
                                rowIndex,
                                "unitPrice",
                                e.target.value
                              )
                            }
                          />
                        ),
                      },
                      {
                        key: "taxPercent",
                        header: "GST %",
                        accessor: ({ row, rowIndex }) => (
                          <input
                            type="text"
                            className="w-16 border rounded-md px-2 py-1 text-sm text-center"
                            value={row.taxPercent?.toString() || ""}
                            onChange={(e) =>
                              handleUpdateItem(
                                rowIndex,
                                "taxPercent",
                                parseFloat(e.target.value) || 0
                              )
                            }
                          />
                        ),
                      },
                      {
                        key: "totalPrice",
                        header: "Total",
                        align: "right",
                        accessor: ({ row }) => {
                          // row.totalPrice is qty * unitPrice (pre-tax) —
                          // add this line's GST so the displayed total
                          // matches what the line actually costs.
                          const lineTax =
                            (row.totalPrice * (row.taxPercent || 0)) / 100;
                          return currencyFormatter(
                            row.totalPrice + lineTax,
                            "IN",
                            2
                          );
                        },
                      },
                      {
                        key: "action",
                        header: "",
                        accessor: ({ rowIndex }) => (
                          <button
                            onClick={() => handleRemoveItem(rowIndex)}
                            className="hover:bg-red-100 text-red-600 px-2 py-2 rounded-md"
                          >
                            <Trash size={16} />
                          </button>
                        ),
                      },
                    ]}
                    data={cart}
                  />
                </div>
              )}
            </div>

            {/* ---------------- PURCHASE SUMMARY ---------------- */}
            <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
              <h3 className="text-sm font-semibold text-gray-700">
                Purchase Summary
              </h3>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <Input
                  label="Tax"
                  value={currencyFormatter(taxValue)}
                  readOnly
                />
                <Input
                  label="Discount (%)"
                  type="number"
                  value={discountPercentage}
                  onChange={(e) => setDiscountPercentage(e.target.value)}
                  placeholder="Enter discount %"
                />
                <Dropdown
                  label="Payment Status"
                  value={paymentStatus}
                  options={[{ label: "Paid", value: "paid" }]}
                  onChange={(val) => setPaymentStatus("paid")}
                  disabled
                />
              </div>

              <Textarea
                label="Remarks"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Additional notes..."
              />

              <div className="flex justify-end border-t pt-3 mt-2">
                <div className="text-right space-y-1">
                  <div className="text-sm text-gray-600">
                    Subtotal: {currencyFormatter(subTotal)}
                  </div>
                  <div className="text-sm text-gray-600">
                    Tax: {currencyFormatter(taxValue)}
                  </div>
                  <div className="text-sm text-gray-600">
                    Discount ({discountPercentValue}%):{" "}
                    {currencyFormatter(discountAmount)}
                  </div>
                  <div className="font-semibold text-gray-800 text-lg">
                    Grand Total: {currencyFormatter(grandTotal)}
                  </div>
                </div>
              </div>
            </div>

            {/* ---------------- ACTION BUTTONS ---------------- */}
            <div className="flex justify-end space-x-3 pb-2">
              <Button onClick={handleClose} variant="outline">
                Cancel
              </Button>
              <Button
                onClick={() => handleSubmit("draft")}
                variant="outline"
                isLoading={create.loading || edit.loading || isLoadingDetails}
                disabled={isLoadingDetails}
              >
                Save as Draft
              </Button>
              <Button
                onClick={() => handleSubmit("completed")}
                className="bg-indigo-600 text-white"
                isLoading={create.loading || edit.loading || isLoadingDetails}
                disabled={isLoadingDetails}
              >
                Place Order
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default PurchaseForm;
